/**
 * Diagnostics page — single-purpose: mint a short-lived debug token the
 * operator can hand to support so they can pull `/api/admin/diag/*` from
 * anywhere without needing a session cookie.
 *
 * Shows:
 *   • Active tokens (prefix-only) + countdown to expiry
 *   • "Generate token" button → copies the freshly-minted token to clipboard
 *   • Quick links to the read-only diag endpoints so the operator can
 *     verify what support will see BEFORE handing the token over.
 */
import React, { useState } from "react";
import useSWR from "swr";
import { Copy, RefreshCw, Trash2, Shield, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function DiagnosticsPage() {
    const { data: tokens, mutate } = useSWR("/api/admin/diag/debug-token", fetcher,
        { refreshInterval: 5000 });
    const { data: envFallbacks } = useSWR("/api/admin/diag/env-fallbacks", fetcher);
    const { data: secrets } = useSWR("/api/admin/diag/secrets-box", fetcher);
    const [minted, setMinted] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [endless, setEndless] = useState(false);

    const generate = async () => {
        setBusy(true); setErr(null); setMinted(null);
        try {
            const r = await api.post("/api/admin/diag/debug-token", null, {
                params: {
                    label: endless ? "support-debug-endless" : "support-debug",
                    ttl_seconds: endless ? 0 : 900,
                },
            });
            setMinted(r.data);
            try { await navigator.clipboard.writeText(r.data.token); } catch (_) { /* clipboard not available — operator can copy manually */ }
            mutate();
        } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        finally { setBusy(false); }
    };

    const revoke = async (prefix) => {
        // We only have the prefix in the listing — operator has to provide
        // the full token to revoke. So we expose a "revoke ALL" button that
        // is the actual recovery action when a token leaks.
        if (!confirm(`Revoke token ${prefix}? You'll have to mint a new one.`)) return;
        // Token-prefix can't be used to delete — instead clear the local
        // "minted" cache so the operator manually re-mints. Real DELETE
        // requires the full token; the operator who minted it has it.
        alert("Use DELETE /api/admin/diag/debug-token/<full-token> with the full value you originally received.");
    };

    return (
        <div className="space-y-6 p-6 max-w-5xl">
            <header className="space-y-1">
                <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
                    <Shield size={20} /> Diagnostics
                </h1>
                <p className="text-xs text-zinc-400">
                    Mint a short-lived read-only token so a support engineer can pull
                    diagnostic data (logs, env-fallbacks, proxmox-probe, node-resolution)
                    from this NexTerminal instance without needing your session cookie.
                    Tokens auto-expire after 15 minutes.
                </p>
            </header>

            <section className="cm-card p-4 space-y-3" data-testid="diag-mint-card">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="cm-h3">Generate a debug token</div>
                        <div className="text-xs text-zinc-500 mt-1">
                            The token grants READ access to all <code>/api/admin/diag/*</code> endpoints
                            for the chosen TTL. Hand the token to support — they curl it directly.
                        </div>
                        <label className="flex items-center gap-2 mt-2 text-xs text-zinc-300 select-none cursor-pointer"
                                data-testid="diag-mint-endless-toggle">
                            <input type="checkbox" checked={endless} onChange={(e) => setEndless(e.target.checked)}
                                    className="accent-blue-500" />
                            <span>Endless (never expires — revoke manually)</span>
                        </label>
                    </div>
                    <button onClick={generate} disabled={busy}
                            className="cm-btn-primary text-sm px-4 py-2 disabled:opacity-50"
                            data-testid="diag-mint-btn">
                        {busy ? "…" : `Generate ${endless ? "endless" : "15-min"} token`}
                    </button>
                </div>
                {err && <div className="text-red-400 text-xs font-mono break-all">{err}</div>}
                {minted && (
                    <div className="mt-3 p-3 border border-emerald-700/60 bg-emerald-950/20 space-y-2">
                        <div className="text-xs text-emerald-300 uppercase tracking-wider">
                            Your new token (already copied to clipboard)
                        </div>
                        <div className="font-mono text-xs break-all bg-zinc-950 p-2 select-all"
                              data-testid="diag-mint-token">
                            {minted.token}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1"
                                onClick={() => navigator.clipboard.writeText(minted.token)}
                            >
                                <Copy size={10} /> Copy again
                            </button>
                            <div className="text-[11px] text-zinc-500">
                                {minted.endless
                                    ? "🔑 ENDLESS — revoke manually in the table below"
                                    : `Expires at ${new Date(minted.expires_at * 1000).toLocaleTimeString()} (${Math.round(minted.ttl_seconds / 60)} min TTL)`}
                            </div>
                        </div>
                        <div className="text-[11px] text-zinc-400 mt-2">
                            Support can now run, for example:
                            <pre className="bg-zinc-950 text-[11px] p-2 mt-1 overflow-x-auto">
{`curl "${window.location.origin}/api/admin/diag/logs?debug_token=${minted.token}&level=ERROR"
curl "${window.location.origin}/api/admin/diag/proxmox/<cluster_id>/node-resolution?debug_token=${minted.token}"
curl "${window.location.origin}/api/admin/diag/env-fallbacks?debug_token=${minted.token}"`}
                            </pre>
                        </div>
                    </div>
                )}
            </section>

            <section className="cm-card p-4 space-y-3" data-testid="diag-active-tokens">
                <div className="flex items-center justify-between">
                    <div className="cm-h3">Currently active tokens</div>
                    <button onClick={() => mutate()} className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1">
                        <RefreshCw size={10} /> Refresh
                    </button>
                </div>
                {(tokens?.active || []).length === 0 ? (
                    <div className="text-xs text-zinc-500">No active tokens.</div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
                            <tr><th className="text-left">Prefix</th><th className="text-left">Label</th><th className="text-right">Expires in</th><th></th></tr>
                        </thead>
                        <tbody>
                            {(tokens.active || []).map((t, i) => (
                                <tr key={i} className="border-t border-zinc-800">
                                    <td className="py-1.5 font-mono">{t.token_prefix}</td>
                                    <td className="py-1.5">{t.label}</td>
                                    <td className="py-1.5 text-right text-zinc-400">
                                        {t.endless
                                            ? <span className="text-amber-400">∞ endless</span>
                                            : `${Math.floor(t.expires_in_seconds / 60)}m ${t.expires_in_seconds % 60}s`}
                                    </td>
                                    <td className="py-1.5 text-right">
                                        <button onClick={() => revoke(t.token_prefix)} className="cm-btn-ghost text-[11px] px-1.5 py-0.5 text-red-400" title="Revoke">
                                            <Trash2 size={10} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <section className="cm-card p-4 space-y-3">
                <div className="cm-h3">Quick health (read-only — uses your session)</div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="p-3 bg-zinc-950 border border-zinc-800">
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Secrets-box</div>
                        <div className="font-mono">
                            {secrets ? `${secrets.canary?.status} · roundtrip ${secrets.roundtrip_ok ? "✓" : "✗"} · key fp ${secrets.key_fingerprint?.slice(0, 8) || "?"}` : "…"}
                        </div>
                    </div>
                    <div className="p-3 bg-zinc-950 border border-zinc-800">
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">PROXMOX_* env-fallbacks</div>
                        <div className="font-mono">
                            {envFallbacks ? `${envFallbacks.set_count} set` : "…"}
                            {envFallbacks?.warning && (
                                <div className="text-amber-300 text-[11px] mt-1 break-all whitespace-pre-wrap">
                                    {envFallbacks.warning}
                                </div>
                            )}
                            {envFallbacks?.values && Object.keys(envFallbacks.values).length > 0 && (
                                <div className="text-[11px] mt-2 space-y-0.5">
                                    {Object.entries(envFallbacks.values).map(([k, v]) => (
                                        <div key={k}><span className="text-zinc-400">{k}=</span>{v}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="text-[11px] text-zinc-500 flex items-center gap-1 pt-1">
                    <ExternalLink size={10} />
                    All diag endpoints are at <code className="text-zinc-300">/api/admin/diag/*</code>
                </div>
            </section>
        </div>
    );
}
